import * as React from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, Checkbox, Dialog, Typography } from "@mui/material";
import { DataGrid, GridCellParams, GridColDef } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import axios from "axios";
import { io } from "socket.io-client";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import { COLORS, STATUS } from "./constants";
import InventoryTabs from "./InventoryTabs";
import ScanHelpTooltip from "./ScanHelpTooltip";
import ActivateGameModal from "./ActivateGameModal";

// Readable label for a DB status value (the Status column shows each settled
// pack's CURRENT inventory status, which continues its normal lifecycle)
const statusLabel = (status: string): string => {
  switch (status) {
    case STATUS.CONFIRM:
      return "Confirm";
    case STATUS.ACTIVE:
      return "Active";
    case STATUS.SOLD_OUT:
      return "Soldout";
    case STATUS.RETURNED:
      return "Returned";
    default:
      return status;
  }
};

interface SettlementScreenProps {
  onGoConfirm?: () => void;
  onGoSales?: () => void;
}

// Settlement screen (Figma node 2381:287). Marks a confirmed/active pack as
// "Settled" (a parallel financial marker that does NOT change the pack's own
// Confirm -> Active -> Sold Out lifecycle), lists settled packs, and can Unsettle
// them (checkbox + Unsettle button, per the client message).
const SettlementScreen: React.FC<SettlementScreenProps> = ({ onGoConfirm, onGoSales }) => {
  //@ts-ignore
  const config = window.config;
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";
  const currencySymbol = config?.currencySymbol ?? "$";
  const shopId = JSON.parse(localStorage.getItem("UserData") || "{}")?.uId;
  const { t } = useTranslation("translation");
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();

  // grid data
  const [settlements, setSettlements] = React.useState<Array<any>>([]);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 10 });
  const [refreshToken, setRefreshToken] = React.useState<number>(0);
  const refresh = () => setRefreshToken(Math.random());

  // entry (scan / manual)
  const [scanCode, setScanCode] = React.useState<string>("");
  const [gameNumber, setGameNumber] = React.useState<string>("");
  const [packNumber, setPackNumber] = React.useState<string>("");
  const [adding, setAdding] = React.useState<boolean>(false);
  const processedScanRef = React.useRef<string>("");

  // filters
  const [fromDateInput, setFromDateInput] = React.useState<Dayjs | null>(null);
  const [toDateInput, setToDateInput] = React.useState<Dayjs | null>(null);
  const [appliedFilters, setAppliedFilters] = React.useState({ fromDate: "", toDate: "" });

  // unsettle selection
  const [selectedIds, setSelectedIds] = React.useState<Array<number>>([]);
  const [unsettling, setUnsettling] = React.useState<boolean>(false);
  const [unsettleOpen, setUnsettleOpen] = React.useState<boolean>(false);

  // Activate popup (for the Activate tab)
  const [activateOpen, setActivateOpen] = React.useState<boolean>(false);
  const [socket, setSocket] = React.useState<any>();

  React.useEffect(() => {
    setLoading(true);
    axios
      .post(`${apiBaseURL}/api/inventory-v2/settlement/fetch`, {
        userId: shopId,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        fromDate: appliedFilters.fromDate,
        toDate: appliedFilters.toDate,
      })
      .then((res: any) => {
        setSettlements(res?.data?.settlements ?? []);
        setTotalCount(res?.data?.totalRecords ?? 0);
      })
      .catch(() => showErrorMessage(t("somethingWentWrong", "Something went wrong")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, appliedFilters, refreshToken]);

  React.useEffect(() => {
    const established = io(apiBaseURL, { autoConnect: true, query: { userId: shopId } });
    setSocket(established);
    return () => {
      established && established.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearEntry = () => {
    setScanCode("");
    setGameNumber("");
    setPackNumber("");
    processedScanRef.current = "";
  };

  // Add a settlement entry. Takes explicit game/pack so a scan can submit freshly
  // parsed values without waiting for the input state to update.
  const submitSettle = (game: string, pack: string) => {
    if (!/^\d{4}$/.test(game)) {
      return showErrorMessage(t("gameNumberFourDigitsError", "The game number must be 4 digits."));
    }
    if (!/^\d{7}$/.test(pack)) {
      return showErrorMessage(t("packNumberSevenDigitsError", "The pack number should be 7 digits."));
    }
    setAdding(true);
    axios
      .post(`${apiBaseURL}/api/inventory-v2/settlement/add`, { userId: shopId, gameNumber: game, packNumber: pack })
      .then(() => {
        showSuccessMessage(t("settlementAddSuccessMsg", "Pack settled successfully"));
        clearEntry();
        refresh();
      })
      .catch((error: any) =>
        showErrorMessage(error?.response?.data?.message ?? t("somethingWentWrong", "Something went wrong")),
      )
      .finally(() => setAdding(false));
  };

  const handleSettle = () => submitSettle(gameNumber, packNumber);

  const processScan = (code: string) => {
    const game = code.substring(0, 4);
    const pack = code.substring(4, 11);
    const key = `${game}-${pack}`;
    if (key === processedScanRef.current) return;
    processedScanRef.current = key;
    setGameNumber(game);
    setPackNumber(pack);
    submitSettle(game, pack);
  };

  const onScanChange = (value: string) => {
    setScanCode(value);
    if (value.length < 14) processedScanRef.current = "";
    if (value.length >= 11) {
      setGameNumber(value.substring(0, 4));
      setPackNumber(value.substring(4, 11));
    }
    if (value.length >= 14) processScan(value);
  };

  const onScanKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = scanCode.trim();
    if (code.length >= 11) processScan(code);
  };

  const onEntryKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (!adding) handleSettle();
  };

  const applyFilter = () => {
    setSelectedIds([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedFilters({
      fromDate: fromDateInput ? fromDateInput.format("YYYY-MM-DD") : "",
      toDate: toDateInput ? toDateInput.format("YYYY-MM-DD") : "",
    });
  };

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openUnsettle = () => {
    if (selectedIds.length === 0) {
      return showErrorMessage(t("unsettleSelectError", "Select a settled pack to unsettle"));
    }
    setUnsettleOpen(true);
  };

  const handleUnsettle = async () => {
    setUnsettling(true);
    try {
      for (const id of selectedIds) {
        await axios.post(`${apiBaseURL}/api/inventory-v2/settlement/remove`, { userId: shopId, id });
      }
      showSuccessMessage(t("unsettleSuccessMsg", "Pack(s) unsettled successfully"));
      setSelectedIds([]);
      setUnsettleOpen(false);
      refresh();
    } catch (error: any) {
      showErrorMessage(error?.response?.data?.message ?? t("somethingWentWrong", "Something went wrong"));
    } finally {
      setUnsettling(false);
    }
  };

  const columns: GridColDef[] = [
    { field: "lotteryName", headerName: t("lotteryNameHeaderText", "Lottery Name"), flex: 1.4, align: "center", headerAlign: "center" },
    { field: "gameNumber", headerName: t("gameNumberHeaderText", "Game #"), flex: 1, align: "center", headerAlign: "center" },
    { field: "packNumber", headerName: t("packNumberHeaderText", "Pack #"), flex: 1, align: "center", headerAlign: "center" },
    {
      field: "gameAmount",
      headerName: t("gameAmountHeaderText", "Game Amount"),
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridCellParams) => <span>{`${currencySymbol}${params.row.gameAmount}`}</span>,
    },
    { field: "settlementDate", headerName: t("settlementDateHeaderText", "Settlement Date"), flex: 1.2, align: "center", headerAlign: "center" },
    {
      field: "status",
      headerName: t("statusHeaderText", "Status"),
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridCellParams) => <span>{statusLabel(params.row.status)}</span>,
    },
    {
      field: "action",
      headerName: t("actionHeaderText", "Action"),
      flex: 0.8,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridCellParams) => (
        <Checkbox
          size='small'
          checked={selectedIds.includes(params.row.id)}
          onChange={() => toggleSelected(params.row.id)}
        />
      ),
    },
  ];

  const fieldSx = {
    backgroundColor: "#fff",
    border: `1px solid #434343`,
    borderRadius: "2px",
    height: "38px",
    px: "10px",
    fontSize: "17px",
    "& .MuiInputBase-input": { padding: 0 },
  } as const;

  return (
    <Box p={2}>
      {/* Header: title + tab navigation */}
      <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap='12px' mb={2}>
        <Typography sx={{ fontSize: "30px", fontWeight: 700, color: "#000" }}>
          {t("settlementTitle", "Settlement")}
        </Typography>
        <InventoryTabs
          active='settlement'
          onConfirmClick={onGoConfirm}
          onActivateClick={() => setActivateOpen(true)}
          onSalesClick={onGoSales}
          onSettlementClick={() => { /* current screen */ }}
        />
      </Box>

      {/* Entry bar: scan / manual entry */}
      <Box display='flex' alignItems='center' justifyContent='center' gap='16px' flexWrap='wrap' mb={2}>
        <Box
          display='flex'
          alignItems='flex-end'
          gap='18px'
          flexWrap='wrap'
          sx={{ backgroundColor: COLORS.panelBg, border: "0.25px solid #434343", p: 2, borderRadius: "2px" }}
        >
          {/* Scan Code + help */}
          <Box>
            <Typography sx={{ fontSize: "17px", color: "#434343", mb: "4px" }}>{t("scanCodeLabel", "Scan Code")}</Typography>
            <Box display='flex' alignItems='center' gap='8px'>
              <Box
                component='input'
                value={scanCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onScanChange(e.target.value)}
                onKeyDown={onScanKeyDown}
                placeholder={t("scanCodePlaceholder", "Scan / enter barcode")}
                sx={{ ...fieldSx, width: "240px", outline: "none" }}
              />
              <ScanHelpTooltip />
            </Box>
          </Box>

          <Typography sx={{ fontSize: "17px", color: "#434343", pb: "8px" }}>{t("or", "OR")}</Typography>

          <Box>
            <Typography sx={{ fontSize: "17px", color: "#434343", mb: "4px" }}>{t("gameNumberLabel", "Game Number")}</Typography>
            <Box
              component='input'
              value={gameNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameNumber(e.target.value)}
              onKeyDown={onEntryKeyDown}
              sx={{ ...fieldSx, width: "150px", outline: "none" }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: "17px", color: "#434343", mb: "4px" }}>{t("packNumberLabel", "Pack Number")}</Typography>
            <Box
              component='input'
              value={packNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPackNumber(e.target.value)}
              onKeyDown={onEntryKeyDown}
              sx={{ ...fieldSx, width: "170px", outline: "none" }}
            />
          </Box>

          <Button
            onClick={handleSettle}
            disabled={adding}
            disableElevation
            sx={{
              backgroundColor: COLORS.blue,
              color: "#fff",
              textTransform: "none",
              borderRadius: "10px",
              height: "38px",
              px: 3,
              fontSize: "16px",
              fontWeight: 600,
              "&:hover": { backgroundColor: COLORS.blue, opacity: 0.9 },
            }}
          >
            {t("enterButton", "Enter")}
          </Button>
        </Box>
      </Box>

      {/* Filter bar */}
      <Box
        display='flex'
        alignItems='center'
        gap='14px'
        flexWrap='wrap'
        sx={{ backgroundColor: COLORS.panelBg, border: `0.25px solid #434343`, p: 1.5, borderRadius: "2px", mb: 2 }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={fromDateInput}
            onChange={(value) => setFromDateInput(value)}
            format='MM/DD/YYYY'
            slotProps={{ textField: { size: "small", sx: { backgroundColor: "#fff", width: "170px" } } }}
          />
          <Typography sx={{ fontSize: "17px" }}>{t("dateRangeTo", "to")}</Typography>
          <DatePicker
            value={toDateInput}
            onChange={(value) => setToDateInput(value)}
            format='MM/DD/YYYY'
            minDate={fromDateInput ?? undefined}
            slotProps={{ textField: { size: "small", sx: { backgroundColor: "#fff", width: "170px" } } }}
          />
        </LocalizationProvider>

        <Button
          onClick={applyFilter}
          disableElevation
          sx={{ backgroundColor: COLORS.grey, color: "#fff", textTransform: "none", borderRadius: "10px", height: "38px", fontSize: "16px", fontWeight: 600, px: 2, "&:hover": { backgroundColor: COLORS.grey, opacity: 0.9 } }}
        >
          {t("filterButton", "Filter")}
        </Button>
        <Box flex={1} />
        <Button
          onClick={openUnsettle}
          disabled={unsettling}
          disableElevation
          sx={{ backgroundColor: COLORS.red, color: "#fff", textTransform: "none", borderRadius: "10px", height: "38px", fontSize: "16px", fontWeight: 600, px: 2, "&:hover": { backgroundColor: COLORS.red, opacity: 0.9 } }}
        >
          {t("unsettleButton", "Unsettle")}
        </Button>
      </Box>

      {/* Table */}
      <Box sx={{ width: "100%", height: "560px" }}>
        <DataGrid
          rows={settlements}
          loading={loading}
          columns={columns}
          rowCount={totalCount}
          pageSizeOptions={[10, 20, 50, 100]}
          pagination
          paginationMode='server'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          getRowId={(row) => row.id}
          disableColumnSelector
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            (settlements.findIndex((r) => r.id === params.id) % 2 === 0) ? "row-dark" : "row-light"
          }
          sx={{
            border: `1px solid ${COLORS.border}`,
            fontSize: "16px",
            "& .MuiDataGrid-columnHeaders": { backgroundColor: "#fff" },
            "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, fontSize: "17px" },
            "& .MuiDataGrid-cell": { fontSize: "16px" },
            "& .row-dark": { backgroundColor: COLORS.rowDark },
            "& .row-light": { backgroundColor: COLORS.rowLight },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
          }}
        />
      </Box>

      <ActivateGameModal
        open={activateOpen}
        onClose={() => setActivateOpen(false)}
        socket={socket}
        apiBaseURL={apiBaseURL}
        shopId={shopId}
        onActivated={refresh}
      />

      {/* Unsettle confirmation */}
      <Dialog open={unsettleOpen} onClose={() => setUnsettleOpen(false)} PaperProps={{ sx: { borderRadius: "10px", p: 1 } }}>
        <Box sx={{ p: 3, maxWidth: "420px" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, mb: 1 }}>{t("unsettleConfirmTitle", "Unsettle selected pack(s)?")}</Typography>
          <Typography sx={{ fontSize: "15px", color: "#434343", mb: 2 }}>
            {t("unsettleConfirmBody", "This removes the settlement entry. The pack's own status is not affected.")}
          </Typography>
          <Box display='flex' justifyContent='flex-end' gap='12px'>
            <Button onClick={() => setUnsettleOpen(false)} disableElevation sx={{ backgroundColor: COLORS.grey, color: "#fff", textTransform: "none", borderRadius: "6px", px: 2.5, fontSize: "15px", fontWeight: 600, "&:hover": { backgroundColor: COLORS.grey, opacity: 0.9 } }}>
              {t("cancel", "Cancel")}
            </Button>
            <Button onClick={handleUnsettle} disabled={unsettling} disableElevation sx={{ backgroundColor: COLORS.red, color: "#fff", textTransform: "none", borderRadius: "6px", px: 2.5, fontSize: "15px", fontWeight: 600, "&:hover": { backgroundColor: COLORS.red, opacity: 0.9 } }}>
              {t("unsettleButton", "Unsettle")}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SettlementScreen;
